import { useEffect, useRef, useState } from "react";
import { danmakuPool } from "@/mocks/danmaku";

interface DanmakuPanelProps {
  liveTitle: string;
}

interface DanmakuLine {
  id: string;
  user: string;
  text: string;
  color: string;
}

export default function DanmakuPanel({ liveTitle }: DanmakuPanelProps) {
  const [lines, setLines] = useState<DanmakuLine[]>(danmakuPool.slice(0, 8));
  const [inputValue, setInputValue] = useState("");
  const [likes, setLikes] = useState(1286);
  const [liked, setLiked] = useState(false);
  const [shareTip, setShareTip] = useState("");
  const listRef = useRef<HTMLDivElement>(null);
  const poolIndex = useRef(8);

  useEffect(() => {
    const timer = setInterval(() => {
      const next = danmakuPool[poolIndex.current % danmakuPool.length];
      poolIndex.current += 1;
      setLines((prev) => {
        const added: DanmakuLine = { ...next, id: `${next.id}-${Date.now()}` };
        return [...prev.slice(-14), added];
      });
    }, 2600);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [lines]);

  const handleSend = () => {
    const text = inputValue.trim();
    if (!text) return;
    setLines((prev) => [
      ...prev.slice(-14),
      { id: `me-${Date.now()}`, user: "我", text, color: "text-primary-300" },
    ]);
    setInputValue("");
  };

  const handleLike = () => {
    if (!liked) {
      setLikes((prev) => prev + 1);
      setLiked(true);
    } else {
      setLikes((prev) => prev - 1);
      setLiked(false);
    }
  };

  const handleShare = async () => {
    const url = `${window.location.origin}${window.location.pathname}?live=${encodeURIComponent(liveTitle)}`;
    try {
      await navigator.clipboard.writeText(url);
      setShareTip("链接已复制，快去分享给朋友吧");
    } catch {
      setShareTip(`请手动复制链接：${url}`);
    }
    window.setTimeout(() => setShareTip(""), 2600);
  };

  return (
    <div className="rounded-lg border border-background-200 bg-background-50">
      <div className="flex items-center justify-between border-b border-background-200 px-4 py-3">
        <h2 className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-950">
          <i className="ri-chat-3-line text-base text-primary-500" />
          互动弹幕
          <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-xs font-normal text-foreground-500">
            演示
          </span>
        </h2>
        <span className="text-xs text-foreground-400">前端模拟滚动，非真实弹幕服务器</span>
      </div>

      <div ref={listRef} className="h-64 space-y-2 overflow-y-auto bg-background-950 px-4 py-3">
        {lines.map((line) => (
          <div key={line.id} className="flex items-start gap-2 text-xs leading-relaxed">
            <span className="shrink-0 font-medium text-foreground-400">{line.user}：</span>
            <span className={line.color}>{line.text}</span>
          </div>
        ))}
      </div>

      <div className="border-t border-background-200 p-4">
        <div className="flex items-center gap-2">
          <input
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSend();
            }}
            maxLength={60}
            type="text"
            placeholder="发一条友善的弹幕吧…"
            className="w-full rounded-md border border-background-300 bg-background-100 px-3 py-2 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400"
          />
          <button
            type="button"
            onClick={handleSend}
            className="shrink-0 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            发送
          </button>
        </div>

        <div className="mt-4 flex items-center gap-3">
          <button
            type="button"
            onClick={handleLike}
            className={`flex items-center gap-1.5 whitespace-nowrap rounded-md px-4 py-2 text-sm font-semibold transition-colors ${
              liked
                ? "bg-primary-500 text-background-50"
                : "bg-secondary-100 text-foreground-700 hover:bg-secondary-200"
            }`}
          >
            <i className={liked ? "ri-heart-fill text-base" : "ri-heart-line text-base"} />
            点赞 {likes}
          </button>
          <button
            type="button"
            onClick={handleShare}
            className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-secondary-100 px-4 py-2 text-sm font-semibold text-foreground-700 transition-colors hover:bg-secondary-200"
          >
            <i className="ri-share-line text-base" />
            分享
          </button>
        </div>

        {shareTip && (
          <p className="mt-3 flex items-center gap-1.5 rounded-md bg-accent-50 px-3 py-2 text-xs text-accent-700">
            <i className="ri-checkbox-circle-line text-base" />
            {shareTip}
          </p>
        )}
      </div>
    </div>
  );
}